import { useNavigate } from "react-router-dom";
import {
  AirVent,
  Car,
  ChevronRight,
  CirclePower,
  Flame,
  LampDesk,
  RotateCw,
  Snowflake,
  Speaker,
  Thermometer,
  Waves,
  WifiOff,
  Wind
} from "lucide-react";
import type {
  CircuitId,
  HomeState,
  MiniSplitState,
  TeslaConnectionStatus,
  ThermostatId,
  ThermostatState
} from "@home-control/shared";
import { useHome } from "../state";
import { relativeTime } from "../format";
import { Badge, Card } from "../components/Card";
import { ActionTile } from "../components/ActionTile";
import { Spinner } from "../components/Spinner";

export function HomePage() {
  const { state, error, refreshing, refresh, hasLoadedOnce } = useHome();

  return (
    <main className="page">
      <header className="home-header">
        <div className="home-header-text">
          <h1 className="home-title">Wooten</h1>
          <p className="home-subtitle">Comfort controls</p>
          <p className="home-updated">{state ? `Updated ${relativeTime(state.updatedAt)}` : "Not connected"}</p>
        </div>
        <button
          type="button"
          className="refresh-button"
          aria-label="Refresh"
          disabled={refreshing}
          onClick={() => void refresh()}
        >
          {refreshing ? <Spinner size={19} /> : <RotateCw size={19} strokeWidth={2.6} />}
        </button>
      </header>

      <ActionsPanel />

      {!state ? (
        <section className="empty-panel">
          <WifiOff size={26} strokeWidth={2.4} style={{ color: "var(--warm)" }} />
          <p className="empty-message">{!hasLoadedOnce ? "Syncing latest climate data." : error || "Not connected."}</p>
          <p className="empty-detail">Controls unlock once live data arrives.</p>
        </section>
      ) : null}

      {state ? <CardGrid state={state} /> : null}
    </main>
  );
}

function ActionsPanel() {
  const { state, command, isPending } = useHome();
  const housePending = isPending("hallway-target") || isPending("bedroom-target");

  async function houseTargets(nextTarget: (current: number) => number) {
    for (const id of ["hallway", "bedroom"] as ThermostatId[]) {
      const target = state?.thermostats[id].targetTempF;
      const base = target ?? 70;
      const next = Math.min(90, Math.max(50, nextTarget(base)));
      await command(
        `${id}-target`,
        { kind: "setThermostatTarget", thermostat: id, targetTempF: next },
        "House command sent"
      );
    }
  }

  return (
    <section className="panel actions-panel" aria-label="Actions">
      <h2 className="panel-title">Actions</h2>
      <div className="actions-grid">
        <ActionTile
          icon={Snowflake}
          title="Make cooler"
          subtitle="House 5° lower for 30 min"
          tint="var(--cool)"
          bg="var(--cool-soft)"
          pending={housePending}
          onPress={() => void houseTargets((current) => current - 5)}
        />
        <ActionTile
          icon={Flame}
          title="Make warmer"
          subtitle="House to 75° until changed"
          tint="var(--warm)"
          bg="var(--warm-soft)"
          pending={housePending}
          onPress={() => void houseTargets(() => 75)}
        />
        <ActionTile
          icon={Wind}
          title="70° + vent"
          subtitle="Tesla cabin to 70° and windows vented"
          tint="var(--ink)"
          bg="var(--surface-alt)"
          pending={isPending("tesla-cabin-70-vent")}
          onPress={() => void command("tesla-cabin-70-vent", { kind: "setTeslaCabinTo70AndVent" })}
        />
      </div>
    </section>
  );
}

function CardGrid({ state }: { state: HomeState }) {
  return (
    <div className="card-grid">
      <ThermostatCard zone={state.thermostats.hallway} />
      <ThermostatCard zone={state.thermostats.bedroom} />
      <MiniSplitCard zone={state.miniSplit} />
      <TeslaCard state={state} />
      <PoolCard state={state} />
      <LampCard state={state} />
      <MusicCard state={state} />
    </div>
  );
}

function zoneTint(mode: string, action: string): string {
  if (action === "heating") return "var(--warm)";
  if (action === "cooling") return "var(--cool)";
  if (mode === "heat") return "var(--warm)";
  if (mode === "off") return "var(--muted)";
  return "var(--cool)";
}

function capitalize(text: string): string {
  return text ? text[0].toUpperCase() + text.slice(1) : text;
}

function ThermostatCard({ zone }: { zone: ThermostatState }) {
  const navigate = useNavigate();
  const tint = zoneTint(zone.mode, zone.action);
  const statusLabels: Record<string, string> = { cooling: "Cool", heating: "Heat", fan: "Fan", off: "Idle" };
  const status = statusLabels[zone.action] ?? capitalize(zone.action);
  return (
    <Card
      icon={Thermometer}
      tint={tint}
      topRight={<ChevronRight size={13} strokeWidth={2.5} className="card-chevron" />}
      title={zone.label}
      value={zone.targetTempF != null ? `${zone.targetTempF}°` : "--°"}
      valueSuffix="set"
      dotColor={tint}
      statusText={`${zone.currentTempF ?? "--"}° now · ${status}`}
      onPress={() => navigate(`/climate/${zone.id}`)}
    />
  );
}

function MiniSplitCard({ zone }: { zone: MiniSplitState }) {
  const navigate = useNavigate();
  const tint = zoneTint(zone.mode, zone.action);
  return (
    <Card
      icon={AirVent}
      tint={tint}
      topRight={<ChevronRight size={13} strokeWidth={2.5} className="card-chevron" />}
      title={zone.label}
      value={zone.targetTempF != null ? `${zone.targetTempF}°` : "--°"}
      valueSuffix="set"
      dotColor={tint}
      statusText={`${zone.currentTempF ?? "--"}° now · ${capitalize(zone.action)}`}
      onPress={() => navigate("/climate/mini-split")}
    />
  );
}

const teslaConnectionLabels: Record<TeslaConnectionStatus, string> = {
  notConnected: "Not connected",
  authRefreshing: "Linked",
  reconnectRequired: "Reconnect required",
  rateLimited: "Rate limited",
  connected: "Linked",
  vehicleAsleep: "Asleep",
  vehicleOnline: "Online",
  vehicleUnavailable: "Vehicle unavailable"
};

function TeslaCard({ state }: { state: HomeState }) {
  const navigate = useNavigate();
  const connection = state.teslaConnection;
  const tesla = state.tesla;
  const status = connection?.status;
  const vehicleOffline = tesla != null && tesla.online === false;

  const warmTint =
    status === "rateLimited" ||
    status === "reconnectRequired" ||
    status === "vehicleAsleep" ||
    status === "vehicleUnavailable" ||
    vehicleOffline;
  const tint = warmTint ? "var(--warm)" : "var(--ink)";

  const badgeText = vehicleOffline
    ? "Offline"
    : tesla?.locked != null
      ? tesla.locked
        ? "Locked"
        : "Unlocked"
      : connection?.connected
        ? "Linked"
        : "Setup";
  const badgeBg = connection?.connected ? "rgba(255, 227, 207, 0.82)" : "rgba(242, 245, 242, 0.82)";

  const canCommand =
    Boolean(connection?.connected) &&
    status !== "notConnected" &&
    status !== "authRefreshing" &&
    status !== "reconnectRequired" &&
    status !== "rateLimited";

  const lastStateAt = tesla?.lastStateAt ?? tesla?.lastSeenAt ?? tesla?.lastUpdatedAt ?? tesla?.updatedAt;
  const statusText = tesla
    ? [
        `${tesla.cabinTempF != null ? Math.round(tesla.cabinTempF) : "--"}° cabin`,
        tesla.windowsOpen ? "windows vented" : "windows closed",
        lastStateAt ? relativeTime(lastStateAt) : null
      ]
        .filter(Boolean)
        .join(" · ")
    : teslaConnectionLabels[status ?? "notConnected"];

  return (
    <Card
      icon={Car}
      tint={tint}
      topRight={<Badge text={badgeText} bg={badgeBg} />}
      title={tesla?.displayName || "Tesla"}
      value={tesla?.batteryLevel != null ? `${tesla.batteryLevel}%` : "--"}
      valueSuffix="battery"
      dotColor={canCommand ? "var(--cool)" : "var(--warm)"}
      statusText={statusText}
      onPress={() => navigate("/tesla")}
    />
  );
}

const circuitOrder: CircuitId[] = ["spa", "pool", "jets", "cleaner", "poolLight"];
const poolHeatModeLabels: Record<string, string> = {
  off: "Off",
  heater: "Heat",
  solar: "Solar",
  solarPreferred: "Solar+"
};

function PoolCard({ state }: { state: HomeState }) {
  const navigate = useNavigate();
  const pool = state.pool;
  const active = circuitOrder.map((id) => pool.circuits[id]).filter((circuit) => circuit?.on);

  const badgeText = active.length === 0 ? "Off" : pool.circuits.spa.on ? "Spa" : pool.circuits.pool.on ? "Pool" : "On";
  const modeLabel = poolHeatModeLabels[pool.heaters.pool.heatMode] ?? "Ready";
  const statusText = active.length
    ? `${modeLabel} · ${active
        .slice(0, 2)
        .map((circuit) => circuit.label)
        .join(", ")}`
    : `${modeLabel} · circuits off`;

  return (
    <Card
      icon={Waves}
      tint="var(--pool)"
      chipOpacity={0.14}
      topRight={<Badge text={badgeText} bg="var(--pool-soft)" />}
      title="Pool"
      value={pool.heaters.pool.currentTempF != null ? `${pool.heaters.pool.currentTempF}°` : "--"}
      valueSuffix="water"
      dotColor={active.length ? "var(--pool)" : "var(--muted)"}
      statusText={statusText}
      onPress={() => navigate("/pool")}
    />
  );
}

function LampCard({ state }: { state: HomeState }) {
  const { command, isPending, canControlDevices } = useHome();
  const lamp = state.plugs?.officeLamp;
  if (!lamp) return null;

  const pending = isPending("plug-officeLamp");
  const tint = lamp.on ? "var(--lamp)" : "var(--muted)";
  const disabled = !canControlDevices || pending || !lamp.online;

  return (
    <Card
      icon={LampDesk}
      tint={tint}
      chipOpacity={0.14}
      topRight={
        pending ? (
          <Spinner size={18} />
        ) : (
          <CirclePower size={22} strokeWidth={lamp.on ? 2.6 : 2.1} style={{ color: tint }} />
        )
      }
      title={lamp.label}
      value={lamp.on ? "On" : "Off"}
      dotColor={lamp.online ? tint : "var(--warm)"}
      statusText={!lamp.online ? "Offline" : lamp.on ? "Tap to turn off" : "Tap to turn on"}
      activeBg={lamp.on ? "var(--lamp-soft)" : undefined}
      activeBorder={lamp.on ? "color-mix(in srgb, var(--lamp) 34%, transparent)" : undefined}
      disabled={disabled}
      onPress={() => void command("plug-officeLamp", { kind: "setPlugState", plug: "officeLamp", on: !lamp.on })}
    />
  );
}

function MusicCard({ state }: { state: HomeState }) {
  const navigate = useNavigate();
  const music = state.music;
  if (!music) return null;

  const active = music.playing || music.paused;
  const tint = active ? "var(--music)" : "var(--muted)";
  const value = !music.available ? "Offline" : music.playing ? "Playing" : music.paused ? "Paused" : "Off";
  const nowPlaying =
    [music.title, music.artist].filter(Boolean).join(" — ") ||
    music.stations.find((station) => station.id === music.stationId)?.label ||
    "Playing";
  const statusText = !music.available ? "Speakers unavailable" : active ? nowPlaying : "Tap to pick a station";

  return (
    <Card
      icon={Speaker}
      tint={tint}
      chipOpacity={0.14}
      topRight={<ChevronRight size={14} strokeWidth={2.75} className="card-chevron" />}
      title="Music"
      value={value}
      dotColor={music.available ? tint : "var(--warm)"}
      statusText={statusText}
      activeBg={active ? "var(--music-soft)" : undefined}
      activeBorder={active ? "color-mix(in srgb, var(--music) 34%, transparent)" : undefined}
      dimmed={!music.available}
      onPress={() => navigate("/music")}
    />
  );
}
